import { useState, useCallback } from 'react';
import type { Raffle, RaffleFavorite } from 'src/types/raffle';
import type {
  RaffleSortOption,
  RaffleFilterState,
  RaffleControlsState,
  EndDateQuickFilter,
} from 'src/types/raffleFilters';
import { DEFAULT_CONTROLS_STATE } from 'src/types/raffleFilters';

const LIVE_STATUSES = new Set(['DRAFT', 'LIVE']);
const PAST_STATUSES = new Set(['ENDED', 'COMPLETED', 'CANCELLED']);

function endDateThreshold(within: EndDateQuickFilter): Date | null {
  if (!within) return null;
  const ms: Record<string, number> = {
    '1H': 1 * 60 * 60_000,
    '6H': 6 * 60 * 60_000,
    '24H': 24 * 60 * 60_000,
    '7D': 7 * 24 * 60 * 60_000,
    '14D': 14 * 24 * 60 * 60_000,
  };
  return new Date(Date.now() + (ms[within] ?? 0));
}

function applySearch(raffles: Raffle[], query: string): Raffle[] {
  if (!query.trim()) return raffles;
  const q = query.toLowerCase();
  return raffles.filter((r) => (r.name ?? '').toLowerCase().includes(q));
}

function applyFilters(raffles: Raffle[], filters: RaffleFilterState): Raffle[] {
  let result = raffles;

  if (filters.raffleType === 'live') {
    result = result.filter((r) => LIVE_STATUSES.has(r.status));
  } else if (filters.raffleType === 'past') {
    result = result.filter((r) => PAST_STATUSES.has(r.status));
  }

  if (filters.ticketPriceMin !== null) {
    result = result.filter((r) => r.ticketPriceSats >= filters.ticketPriceMin!);
  }
  if (filters.ticketPriceMax !== null) {
    result = result.filter((r) => r.ticketPriceSats <= filters.ticketPriceMax!);
  }

  const threshold = endDateThreshold(filters.endDateWithin);
  if (threshold) {
    const now = new Date();
    result = result.filter((r) => {
      const end = new Date(r.endDate);
      return end >= now && end <= threshold;
    });
  }

  return result;
}

function applySort(raffles: Raffle[], sort: RaffleSortOption): Raffle[] {
  const sorted = [...raffles];
  switch (sort) {
    case 'default':
    case 'recently_added':
      return sorted.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    case 'expiring_soon':
      return sorted.sort(
        (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
      );
    case 'tickets_remaining_asc':
      return sorted.sort(
        (a, b) => (a.supply - a.ticketsSold) - (b.supply - b.ticketsSold),
      );
    case 'tickets_remaining_desc':
      return sorted.sort(
        (a, b) => (b.supply - b.ticketsSold) - (a.supply - a.ticketsSold),
      );
    case 'ticket_price_asc':
      return sorted.sort((a, b) => a.ticketPriceSats - b.ticketPriceSats);
    case 'ticket_price_desc':
      return sorted.sort((a, b) => b.ticketPriceSats - a.ticketPriceSats);
    case 'floor_price_asc':
      return sorted.sort((a, b) => a.totalTicketValue - b.totalTicketValue);
    case 'floor_price_desc':
      return sorted.sort((a, b) => b.totalTicketValue - a.totalTicketValue);
    default:
      return sorted;
  }
}

export function useRaffleControls() {
  const [controls, setControls] = useState<RaffleControlsState>(DEFAULT_CONTROLS_STATE);

  const setSearchQuery = useCallback(
    (query: string) => setControls((prev) => ({ ...prev, searchQuery: query })),
    [],
  );

  const setSort = useCallback(
    (sort: RaffleSortOption) => setControls((prev) => ({ ...prev, sort })),
    [],
  );

  const setFilters = useCallback(
    (filters: RaffleFilterState) => setControls((prev) => ({ ...prev, filters })),
    [],
  );

  const resetControls = useCallback(() => setControls(DEFAULT_CONTROLS_STATE), []);

  const processRaffles = useCallback(
    (raffles: Raffle[]): Raffle[] => {
      let result = applySearch(raffles, controls.searchQuery);
      result = applyFilters(result, controls.filters);
      result = applySort(result, controls.sort);
      return result;
    },
    [controls],
  );

  const processFavorites = useCallback(
    (favorites: RaffleFavorite[]): RaffleFavorite[] => {
      const raffles = favorites.map((f) => f.raffle);
      const processed = processRaffles(raffles);
      const idToFav = new Map(favorites.map((f) => [f.raffle.id, f]));
      return processed.map((r) => idToFav.get(r.id)!).filter(Boolean);
    },
    [processRaffles],
  );

  return {
    controls,
    setSearchQuery,
    setSort,
    setFilters,
    resetControls,
    processRaffles,
    processFavorites,
  };
}
